import React, { useState, useCallback, useRef, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import SpecialityAutosuggest from './SpecialityAutosuggest';

// ── Responsive hook ──────────────────────────────────────────────────────────
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
};

// ── Global styles injected once ──────────────────────────────────────────────
const GlobalStyle = () => {
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      * { box-sizing: border-box; }
      body { margin: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
      input, button, select { font-family: inherit; }
      input:focus, select:focus { border-color: #06B6D4 !important; box-shadow: 0 0 0 3px rgba(6,182,212,0.15); }

      /* ── Role Selection Page ── */
      .register-container {
        min-height: 100vh;
        display: flex;
        align-items: center;
        justify-content: center;
        background: linear-gradient(135deg, #0f4c75 0%, #1b6ca8 30%, #0d7a6e 65%, #11998e 100%);
        padding: 20px;
        position: relative;
        overflow: hidden;
      }
      .register-container::before {
        content: '';
        position: absolute;
        top: -120px; left: -120px;
        width: 480px; height: 480px;
        background: radial-gradient(circle, rgba(17,153,142,0.25) 0%, transparent 70%);
        border-radius: 50%;
        pointer-events: none;
      }
      .register-container::after {
        content: '';
        position: absolute;
        bottom: -100px; right: -80px;
        width: 400px; height: 400px;
        background: radial-gradient(circle, rgba(27,108,168,0.3) 0%, transparent 70%);
        border-radius: 50%;
        pointer-events: none;
      }

      .register-content {
        max-width: 900px;
        width: 100%;
        text-align: center;
        position: relative;
        z-index: 1;
      }

      .register-header-badge {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        background: rgba(255,255,255,0.15);
        border: 1px solid rgba(255,255,255,0.3);
        border-radius: 30px;
        padding: 6px 18px;
        font-size: 13px;
        color: white;
        font-weight: 600;
        letter-spacing: 0.04em;
        margin-bottom: 18px;
        backdrop-filter: blur(8px);
      }

      .register-title {
        font-size: 3rem;
        color: white;
        margin: 0 0 12px;
        font-weight: 800;
        text-shadow: 0 2px 20px rgba(0,0,0,0.2);
        letter-spacing: -0.02em;
      }

      .register-subtitle {
        font-size: 1.05rem;
        color: rgba(255,255,255,0.8);
        margin: 0 0 40px;
        font-weight: 400;
      }

      .role-cards-wrapper {
        display: flex;
        justify-content: center;
        gap: 24px;
        flex-wrap: wrap;
        margin-bottom: 36px;
      }

      .role-card {
        background: white;
        border-radius: 20px;
        padding: 36px 28px;
        width: 240px;
        cursor: pointer;
        transition: all 0.3s ease;
        box-shadow: 0 10px 40px rgba(0,0,0,0.15);
        border: 3px solid transparent;
        position: relative;
        overflow: hidden;
      }
      .role-card::after {
        content: '';
        position: absolute;
        top: 0; left: 0; right: 0;
        height: 4px;
        background: linear-gradient(90deg, #0d7a6e, #1b6ca8);
        opacity: 0;
        transition: opacity 0.3s;
      }
      .role-card:hover {
        transform: translateY(-10px);
        box-shadow: 0 20px 50px rgba(0,0,0,0.25);
        border-color: #11998e;
      }
      .role-card:hover::after { opacity: 1; }

      .role-icon {
        font-size: 60px;
        margin-bottom: 16px;
        display: block;
      }
      .role-card h3 {
        font-size: 1.4rem;
        color: #1F2937;
        font-weight: 700;
        margin: 0 0 8px;
        text-transform: capitalize;
      }
      .role-card p {
        font-size: 13px;
        color: #6B7280;
        margin: 0 0 20px;
      }
      .role-card-btn {
        width: 100%;
        padding: 11px 0;
        border-radius: 10px;
        font-weight: 700;
        font-size: 14px;
        border: none;
        cursor: pointer;
        background: linear-gradient(135deg, #0d7a6e, #1b6ca8);
        color: white;
        transition: opacity 0.2s;
        min-height: 42px;
      }
      .role-card-btn:hover { opacity: 0.88; }

      .role-badge {
        position: absolute;
        top: -10px; right: -10px;
        background: #EF4444;
        color: white;
        border-radius: 20px;
        padding: 3px 10px;
        font-size: 11px;
        font-weight: 700;
        box-shadow: 0 2px 8px rgba(239,68,68,0.45);
      }

      .already-registered {
        background: rgba(255,255,255,0.15);
        backdrop-filter: blur(8px);
        border: 1px solid rgba(255,255,255,0.25);
        padding: 14px 24px;
        border-radius: 12px;
        display: inline-block;
      }
      .already-registered p {
        margin: 0;
        font-size: 14px;
        color: rgba(255,255,255,0.9);
      }
      .login-link {
        color: white;
        font-weight: 700;
        cursor: pointer;
        text-decoration: underline;
        text-underline-offset: 3px;
      }
      .login-link:hover { opacity: 0.8; }

      /* ── Modal grid helpers ── */
      .role-cards { display: flex; justify-content: center; gap: 24px; flex-wrap: wrap; }
      @media (max-width: 600px) {
        .role-cards { flex-direction: column; align-items: center; gap: 16px; }
        .role-card  { width: 100% !important; max-width: 340px; }
        .register-title { font-size: 2rem; }
        .role-cards-wrapper { gap: 16px; }
      }

      .two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
      .three-col { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14px; }
      @media (max-width: 500px) {
        .two-col   { grid-template-columns: 1fr !important; gap: 0; }
        .three-col { grid-template-columns: 1fr !important; gap: 0; }
      }

      @media (max-width: 420px) { .step-label { display: none; } }

      .modal-box {
        background: white; border-radius: 14px;
        width: 100%; max-width: 520px; max-height: 92vh; overflow-y: auto;
        box-shadow: 0 25px 60px rgba(0,0,0,0.25);
      }
      @media (max-width: 600px) {
        .modal-box { border-radius: 0 !important; max-height: 100vh !important; height: 100vh; }
      }

      .overlay-bg {
        position: fixed; top:0; left:0; right:0; bottom:0;
        background: rgba(0,0,0,0.55);
        display: flex; align-items: center; justify-content: center;
        padding: 20px; z-index: 1000;
      }
      @media (max-width: 600px) { .overlay-bg { padding: 0 !important; align-items: flex-start; } }

      .qr-inner { padding: 28px 26px; text-align: center; }
      @media (max-width: 480px) { .qr-inner { padding: 20px 16px; } }

      .form-inner { padding: 20px 22px; }
      @media (max-width: 480px) { .form-inner { padding: 16px 14px; } }

      .modal-header { padding: 16px 20px; display:flex; justify-content:space-between; align-items:center; }
      @media (max-width:480px) { .modal-header { padding: 14px 16px; } }

      select {
        width: 100%;
        padding: 12px 14px;
        border: 2px solid #E5E7EB;
        border-radius: 9px;
        font-size: 16px;
        outline: none;
        background: white;
        cursor: pointer;
        transition: border-color 0.2s;
        WebkitAppearance: none;
        appearance: none;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%236B7280' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 14px center;
        padding-right: 38px;
      }

      .gmaps-dropdown::-webkit-scrollbar { width: 4px; }
      .gmaps-dropdown::-webkit-scrollbar-track { background: #F9FAFB; }
      .gmaps-dropdown::-webkit-scrollbar-thumb { background: #A5F3FC; border-radius: 4px; }

      /* ── Features Banner ── */
      .features-banner {
        background: linear-gradient(135deg, #0f2027 0%, #16404d 45%, #0d5c63 100%);
        border-radius: 14px;
        padding: 18px 20px 20px;
        margin-bottom: 20px;
        position: relative;
        overflow: hidden;
        box-shadow: 0 10px 30px rgba(13, 92, 99, 0.35);
      }
      .features-banner::before {
        content: '';
        position: absolute;
        top: -40px; right: -40px;
        width: 160px; height: 160px;
        background: rgba(255,255,255,0.06);
        border-radius: 50%;
        pointer-events: none;
      }
      .features-banner::after {
        content: '';
        position: absolute;
        bottom: -30px; left: -20px;
        width: 120px; height: 120px;
        background: rgba(255,255,255,0.04);
        border-radius: 50%;
        pointer-events: none;
      }
      .features-headline {
        font-family: Georgia, serif;
        font-size: 14px;
        font-weight: 700;
        color: #fef3c7;
        margin: 0 0 14px;
        display: flex;
        align-items: center;
        gap: 8px;
        position: relative;
        z-index: 1;
        letter-spacing: 0.01em;
      }
      .features-headline-icon {
        background: rgba(255,255,255,0.18);
        border-radius: 8px;
        padding: 4px 8px;
        font-size: 15px;
        flex-shrink: 0;
      }
      .features-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 7px 12px;
        position: relative;
        z-index: 1;
      }
      @media (max-width: 400px) {
        .features-grid { grid-template-columns: 1fr; }
      }
      .feature-pill {
        display: flex;
        align-items: center;
        gap: 7px;
        background: rgba(255,255,255,0.1);
        border-radius: 8px;
        padding: 7px 10px;
        border: 1px solid rgba(255,255,255,0.15);
      }
      .feature-pill-icon { font-size: 14px; flex-shrink: 0; }
      .feature-pill-text {
        font-family: Georgia, serif;
        font-size: 11px;
        color: rgba(255,255,255,0.93);
        font-weight: 500;
        line-height: 1.35;
        flex: 1;
        letter-spacing: 0.01em;
      }
      .feature-check {
        flex-shrink: 0;
        width: 15px; height: 15px;
        background: rgba(255,255,255,0.2);
        border-radius: 50%;
        display: flex; align-items: center; justify-content: center;
        font-size: 8px; color: #a7f3d0;
        margin-left: auto;
      }

      /* ── QR Section ── */
      .qr-card {
        display: inline-block;
        position: relative;
        padding: 12px;
        border-radius: 16px;
        background: white;
        border: 1.5px solid #bae6fd;
        box-shadow: 0 2px 16px rgba(14,116,144,0.14);
      }
      .qr-card img { display: block; border-radius: 8px; }
      .qr-scan-label {
        position: absolute;
        bottom: -11px; left: 50%; transform: translateX(-50%);
        background: linear-gradient(135deg, #0D5C63, #0A4348);
        color: white;
        font-size: 9px; font-weight: 800;
        padding: 3px 14px; border-radius: 20px;
        white-space: nowrap; letter-spacing: 0.06em;
      }
      .upi-pill {
        display: inline-flex; align-items: center; gap: 8px;
        margin-top: 22px;
        background: linear-gradient(135deg, #fff7ed, #fef3c7);
        border: 1.5px solid #fed7aa; border-radius: 10px;
        padding: 10px 18px; font-size: 13px; color: #78350f;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);
  return null;
};

// ── Shared style helpers ─────────────────────────────────────────────────────
const mkHeader = (bg) => ({
  backgroundColor: bg,
  borderTopLeftRadius: 14, borderTopRightRadius: 14,
});
const headerTitle = { fontSize: 20, fontWeight: 'bold', color: 'white', margin: 0 };
const closeBtn = {
  background: 'rgba(255,255,255,0.2)', border: 'none',
  color: 'white', cursor: 'pointer', padding: '7px 12px',
  borderRadius: 8, fontSize: 20, lineHeight: 1, minWidth: 38, minHeight: 38,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

// ── India States ─────────────────────────────────────────────────────────────
const INDIA_STATES = [
  'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh',
  'Goa','Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka',
  'Kerala','Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram',
  'Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana',
  'Tripura','Uttar Pradesh','Uttarakhand','West Bengal',
  'Andaman and Nicobar Islands','Chandigarh','Dadra and Nagar Haveli and Daman and Diu',
  'Delhi','Jammu and Kashmir','Ladakh','Lakshadweep','Puducherry'
];

const COUNTRIES = [
  'India','United States','United Kingdom','Canada','Australia','Germany',
  'France','Singapore','UAE','Other'
];

// ── Google Maps Loader ───────────────────────────────────────────────────────
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

let gmapsScriptLoaded = false;
let gmapsScriptLoading = false;
const gmapsCallbacks = [];

const loadGoogleMaps = () =>
  new Promise((resolve) => {
    if (window.google?.maps?.places) { resolve(); return; }
    if (gmapsScriptLoaded) { resolve(); return; }
    gmapsCallbacks.push(resolve);
    if (!gmapsScriptLoading) {
      gmapsScriptLoading = true;
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        gmapsScriptLoaded = true;
        gmapsCallbacks.forEach(cb => cb());
        gmapsCallbacks.length = 0;
      };
      document.head.appendChild(script);
    }
  });

const getAddrComponent = (components, type) => {
  const c = components?.find(c => c.types.includes(type));
  return c?.long_name || '';
};

// ── Google Places Autocomplete Input ─────────────────────────────────────────
const PlacesInput = ({
  label, value, onChange, onPlaceSelected, placeholder,
  required = true, types, componentRestrictions, icon,
}) => {
  const [inputVal, setInputVal]       = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showDrop, setShowDrop]       = useState(false);
  const [activeIdx, setActiveIdx]     = useState(-1);
  const [mapsReady, setMapsReady]     = useState(false);
  const [focused, setFocused]         = useState(false);
  const svcRef      = useRef(null);
  const tokenRef    = useRef(null);
  const debounceRef = useRef(null);
  const wrapperRef  = useRef(null);

  useEffect(() => { setInputVal(value || ''); }, [value]);

  useEffect(() => {
    loadGoogleMaps().then(() => {
      svcRef.current   = new window.google.maps.places.AutocompleteService();
      tokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
      setMapsReady(true);
    });
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setShowDrop(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchSuggestions = useCallback((query) => {
    if (!mapsReady || !svcRef.current || query.length < 2) { setSuggestions([]); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const request = { input: query, sessionToken: tokenRef.current };
      if (types) request.types = types;
      if (componentRestrictions) request.componentRestrictions = componentRestrictions;
      svcRef.current.getPlacePredictions(request, (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setSuggestions(predictions.slice(0, 5)); setShowDrop(true);
        } else { setSuggestions([]); setShowDrop(false); }
      });
    }, 250);
  }, [mapsReady, types, componentRestrictions]);

  const handleSelect = useCallback((suggestion) => {
    const mainText = suggestion.structured_formatting?.main_text || suggestion.description;
    setInputVal(mainText); onChange(mainText);
    setSuggestions([]); setShowDrop(false); setActiveIdx(-1);
    tokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    if (onPlaceSelected) {
      const svc = new window.google.maps.places.PlacesService(document.createElement('div'));
      svc.getDetails(
        { placeId: suggestion.place_id, fields: ['address_components', 'formatted_address', 'geometry'] },
        (place, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK) onPlaceSelected(place);
        }
      );
    }
  }, [onChange, onPlaceSelected]);

  const handleKeyDown = (e) => {
    if (!showDrop || suggestions.length === 0) return;
    if (e.key === 'ArrowDown')                    { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, suggestions.length - 1)); }
    else if (e.key === 'ArrowUp')                 { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && activeIdx >= 0) { e.preventDefault(); handleSelect(suggestions[activeIdx]); }
    else if (e.key === 'Escape')                  setShowDrop(false);
  };

  const dropOpen = showDrop && suggestions.length > 0;

  return (
    <div ref={wrapperRef} style={{ marginBottom: 14, position: 'relative' }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
        {label} {required && <span style={{ color: '#EF4444' }}>*</span>}
      </label>
      <div style={{ position: 'relative' }}>
        {icon && (
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 14, pointerEvents: 'none', zIndex: 1 }}>{icon}</span>
        )}
        <input
          type="text" value={inputVal}
          onChange={(e) => {
            const val = e.target.value;
            setInputVal(val); onChange(val); setActiveIdx(-1);
            if (val) fetchSuggestions(val); else { setSuggestions([]); setShowDrop(false); }
          }}
          onFocus={() => { setFocused(true); if (inputVal && suggestions.length > 0) setShowDrop(true); }}
          onBlur={() => setFocused(false)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || `Search ${label}...`}
          autoComplete="off"
          style={{
            width: '100%',
            padding: icon ? '12px 36px 12px 34px' : '12px 36px 12px 14px',
            border: `2px solid ${focused ? '#06B6D4' : '#E5E7EB'}`,
            borderRadius: dropOpen ? '9px 9px 0 0' : 9,
            fontSize: 16, outline: 'none',
            transition: 'border-color 0.2s, box-shadow 0.2s',
            boxShadow: focused ? '0 0 0 3px rgba(6,182,212,0.15)' : 'none',
            WebkitAppearance: 'none', background: 'white',
          }}
        />
        {inputVal && (
          <button type="button"
            onMouseDown={(e) => { e.preventDefault(); setInputVal(''); onChange(''); setSuggestions([]); setShowDrop(false); }}
            style={{
              position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
              background: '#E5E7EB', border: 'none', borderRadius: '50%',
              width: 20, height: 20, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, color: '#6B7280', padding: 0, lineHeight: 1,
            }}>✕</button>
        )}
      </div>
      {dropOpen && (
        <div className="gmaps-dropdown" style={{
          position: 'absolute', top: '100%', left: 0, right: 0,
          background: 'white', border: '2px solid #06B6D4', borderTop: 'none',
          borderRadius: '0 0 9px 9px', boxShadow: '0 8px 24px rgba(6,182,212,0.15)',
          zIndex: 9999, overflow: 'hidden', maxHeight: 220, overflowY: 'auto',
        }}>
          {suggestions.map((s, i) => (
            <div key={s.place_id}
              onMouseDown={(e) => { e.preventDefault(); handleSelect(s); }}
              style={{
                padding: '10px 14px', cursor: 'pointer',
                background: i === activeIdx ? '#ECFEFF' : 'white',
                borderBottom: '1px solid #F3F4F6',
                display: 'flex', alignItems: 'flex-start', gap: 10,
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#ECFEFF'}
              onMouseLeave={e => e.currentTarget.style.background = i === activeIdx ? '#ECFEFF' : 'white'}
            >
              <span style={{ fontSize: 14, marginTop: 1, flexShrink: 0 }}>📍</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#1F2937', lineHeight: 1.3 }}>
                  {s.structured_formatting?.main_text || s.description}
                </div>
                {s.structured_formatting?.secondary_text && (
                  <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>{s.structured_formatting.secondary_text}</div>
                )}
              </div>
            </div>
          ))}
          <div style={{
            padding: '5px 12px', background: '#F9FAFB', borderTop: '1px solid #F3F4F6',
            display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 4,
          }}>
            <span style={{ fontSize: 9, color: '#9CA3AF' }}>powered by</span>
            <span style={{ fontSize: 10, fontWeight: 700, color: '#4B5563' }}>Google</span>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Step Indicator ────────────────────────────────────────────────────────────
const StepIndicator = ({ activeIndex, steps }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 22 }}>
    {steps.map((s, i) => (
      <React.Fragment key={s}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, opacity: i === activeIndex ? 1 : 0.35 }}>
          <div style={{
            width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
            background: i === activeIndex ? '#0D5C63' : '#E5E7EB',
            color: i === activeIndex ? 'white' : '#9CA3AF',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 700,
          }}>{i + 1}</div>
          <span className="step-label" style={{
            fontSize: 11, whiteSpace: 'nowrap',
            color: i === activeIndex ? '#0A4348' : '#9CA3AF',
            fontWeight: i === activeIndex ? 700 : 400,
          }}>{s}</span>
        </div>
        {i < steps.length - 1 && <div style={{ flex: 1, height: 1, background: '#E5E7EB', minWidth: 8 }} />}
      </React.Fragment>
    ))}
  </div>
);

// ── Features Banner (Doctor) ─────────────────────────────────────────────────
const DOCTOR_FEATURES = [
  { icon: '📅', text: 'Seamless & user-friendly online appointment booking system' },
  { icon: '🔐', text: 'Secure digital access to patient documents anytime, anywhere' },
  { icon: '📋', text: 'Efficient storage & management of patient treatment history' },
  { icon: '💼', text: 'Regular updates on current job opportunities in clinics, hospitals, & the healthcare sector' },
  { icon: '📢', text: 'Ability to post & promote job openings within your clinic or hospital' },
  { icon: '✈️', text: 'Access to Medical Tourism opportunities by connecting with international patients' },
  { icon: '🎓', text: 'Expert guidance for international healthcare career opportunities' },
  { icon: '📅', text: 'Timely updates on upcoming medical conferences & events' },
  { icon: '📧', text: 'Option to obtain a dedicated professional email address for work purposes' },
  { icon: '📝', text: 'Platform to publish & share your blogs, articles, & videos' },
  { icon: '💊', text: 'Facility to send prescriptions digitally to patients for better convenience' },
  { icon: '🚚', text: 'Comprehensive door-to-door marketing support to enhance your reach' },
  { icon: '📱', text: 'Strategic social media marketing to strengthen your professional presence' },
  { icon: '📰', text: 'Stay informed with the latest medical news & current affairs' },
];

const FeaturesBanner = () => (
  <div className="features-banner">
    <p className="features-headline">
      <span className="features-headline-icon">🚀</span>
      Make Your Clinic / Hospital Go Digital
    </p>
    <div className="features-grid">
      {DOCTOR_FEATURES.map(({ icon, text }) => (
        <div key={text} className="feature-pill">
          <span className="feature-pill-icon">{icon}</span>
          <span className="feature-pill-text">{text}</span>
          <span className="feature-check">✓</span>
        </div>
      ))}
    </div>
  </div>
);

// ── Doctor Payment Step 1 – QR Code ─────────────────────────────────────────
const DoctorPaymentQRStep = ({ onNext, onBack }) => {
  const QR_URL =
    'https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=upi://pay?pa=trustyoudoctor@upi%26pn=TrustYouDoctor%26am=1200%26cu=INR%26tn=DoctorAnnualSubscription';

  return (
    <div className="overlay-bg">
      <div className="modal-box" style={{ maxWidth: 460 }}>
        <div className="modal-header" style={mkHeader('#0D5C63')}>
          <h2 style={headerTitle}>Pay Subscription Fee</h2>
          <button onClick={onBack} style={closeBtn}>✕</button>
        </div>

        <div className="qr-inner">
          <div style={{
            display: 'inline-block', background: '#E6F4F1', border: '1px solid #A7D8D3',
            borderRadius: 20, padding: '6px 18px', marginBottom: 16,
            fontSize: 13, fontWeight: 600, color: '#0D5C63',
          }}>
            👨‍⚕️ Doctor Annual Plan
          </div>

          <div style={{ marginBottom: 16 }}>
            <span style={{ fontSize: 42, fontWeight: 800, color: '#1F2937' }}>₹1,200</span>
            <span style={{ fontSize: 15, color: '#6B7280', marginLeft: 6 }}>/year</span>
          </div>

          {/* ── Premium Features Banner ── */}
          <FeaturesBanner />

          <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 12 }}>
            Scan using GPay, PhonePe, Paytm or any UPI app
          </p>

          <div className="qr-card">
            <img src={QR_URL} alt="Payment QR" width={170} height={170} />
            <span className="qr-scan-label">SCAN &amp; PAY</span>
          </div>

          <div>
            <div className="upi-pill">
              💳 UPI ID: <strong style={{ color: '#c2410c', fontSize: 14, letterSpacing: '0.02em' }}>trustyoudoctor@upi</strong>
            </div>
          </div>

          <button onClick={onNext} style={{
            marginTop: 22, width: '100%', padding: '14px',
            background: 'linear-gradient(135deg,#0D5C63,#0A4348)',
            color: 'white', border: 'none', borderRadius: 10,
            fontWeight: 700, fontSize: 15, cursor: 'pointer',
            boxShadow: '0 4px 14px rgba(13,92,99,0.4)', minHeight: 48,
          }}>
            I've Paid — Upload Screenshot →
          </button>
          <p style={{ marginTop: 10, fontSize: 12, color: '#9CA3AF' }}>
            Upload payment proof on the next step to complete registration
          </p>
        </div>
      </div>
    </div>
  );
};

// ── Doctor Payment Step 2 – Screenshot Upload ────────────────────────────────
const DoctorUploadStep = ({ onSubmit, onBack, loading }) => {
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const fileRef = useRef();

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) { toast.error('Please upload an image file'); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error('File too large. Max 5 MB.'); return; }
    setFile(f);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f) handleFile({ target: { files: [f] } });
  };

  return (
    <div className="overlay-bg">
      <div className="modal-box" style={{ maxWidth: 480 }}>
        <div className="modal-header" style={mkHeader('#0D5C63')}>
          <button onClick={onBack} style={{ ...closeBtn, fontSize: 13, padding: '7px 14px', minWidth: 'unset' }}>
            ← Back
          </button>
          <h2 style={{ ...headerTitle, flex: 1, textAlign: 'center' }}>Upload Payment Proof</h2>
          <div style={{ width: 72 }} />
        </div>

        <div className="form-inner">
          <StepIndicator activeIndex={2} steps={['Fill Details', 'Pay ₹1,200', 'Upload Proof', 'Done']} />

          <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 16 }}>
            Upload a clear screenshot of your UPI payment confirmation (JPG, PNG, WEBP — max 5 MB).
          </p>

          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            onClick={() => fileRef.current.click()}
            style={{
              border: `2px dashed ${preview ? '#0D5C63' : '#D1D5DB'}`,
              borderRadius: 12, padding: '22px 16px', textAlign: 'center',
              cursor: 'pointer', background: preview ? '#E6F4F1' : '#F9FAFB',
              transition: 'all 0.2s', marginBottom: 16, minHeight: 140,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} style={{ display: 'none' }} />
            {preview ? (
              <>
                <img src={preview} alt="Preview" style={{
                  maxHeight: 180, maxWidth: '100%', borderRadius: 8,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)', marginBottom: 8,
                }} />
                <p style={{ fontSize: 12, color: '#0D5C63', fontWeight: 500, margin: 0 }}>
                  ✅ {file?.name} — tap to change
                </p>
              </>
            ) : (
              <>
                <div style={{ fontSize: 36, marginBottom: 8 }}>📤</div>
                <p style={{ fontWeight: 600, color: '#374151', marginBottom: 4, fontSize: 14, margin: '0 0 4px' }}>
                  Tap to upload screenshot
                </p>
                <p style={{ fontSize: 12, color: '#9CA3AF', margin: 0 }}>JPG, PNG, WEBP up to 5 MB</p>
              </>
            )}
          </div>

          <div style={{
            background: '#FFFBEB', border: '1px solid #FCD34D',
            borderRadius: 8, padding: '10px 14px', fontSize: 12,
            color: '#92400E', marginBottom: 20, lineHeight: 1.5,
          }}>
            ⚠️ Screenshot must clearly show <strong>transaction ID, amount (₹1,200)</strong> and <strong>date</strong>.
          </div>

          <button
            onClick={() => {
              if (!file) { toast.error('Please upload your payment screenshot first'); return; }
              onSubmit(file);
            }}
            disabled={loading}
            style={{
              width: '100%', padding: '14px',
              background: loading ? '#9CA3AF' : 'linear-gradient(135deg,#0D5C63,#0A4348)',
              color: 'white', border: 'none', borderRadius: 10,
              fontWeight: 700, fontSize: 15, minHeight: 48,
              cursor: loading ? 'not-allowed' : 'pointer',
              boxShadow: loading ? 'none' : '0 4px 14px rgba(13,92,99,0.4)',
            }}
          >
            {loading ? '⏳ Completing Registration...' : '✅ Complete Registration'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Input Field ───────────────────────────────────────────────────────────────
const InputField = ({ label, type = 'text', name, value, onChange, maxLength, required = true, fieldKey, showPassword, setShowPassword }) => {
  const isPassword = type === 'password';
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
        {label} {required && <span style={{ color: '#EF4444' }}>*</span>}
      </label>
      <div style={{ position: 'relative' }}>
        <input
          name={name}
          type={isPassword ? (showPassword[fieldKey] ? 'text' : 'password') : type}
          value={value}
          onChange={onChange}
          maxLength={maxLength}
          required={required}
          autoComplete={isPassword ? 'new-password' : 'off'}
          style={{
            width: '100%',
            padding: isPassword ? '12px 50px 12px 14px' : '12px 14px',
            border: '2px solid #E5E7EB', borderRadius: 9,
            fontSize: 16, outline: 'none', transition: 'border-color 0.2s',
            WebkitAppearance: 'none',
          }}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShowPassword(prev => ({ ...prev, [fieldKey]: !prev[fieldKey] }))}
            style={{
              position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#9CA3AF', fontSize: 12, padding: '4px 6px',
              minWidth: 40, minHeight: 40,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {showPassword[fieldKey] ? 'Hide' : 'Show'}
          </button>
        )}
      </div>
    </div>
  );
};

// ── Select Field ──────────────────────────────────────────────────────────────
const SelectField = ({ label, name, value, onChange, options, required = true, placeholder }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
      {label} {required && <span style={{ color: '#EF4444' }}>*</span>}
    </label>
    <select name={name} value={value} onChange={onChange} required={required}>
      <option value="">{placeholder || `Select ${label}`}</option>
      {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
    </select>
  </div>
);

// ── Address Section ───────────────────────────────────────────────────────────
const AddressSection = ({ data, onChange }) => {
  const set = (name, value) => onChange({ target: { name, value } });

  const handlePlaceSelected = (place) => {
    const comps = place.address_components || [];
    const city     = getAddrComponent(comps, 'locality') || getAddrComponent(comps, 'sublocality_level_1');
    const district = getAddrComponent(comps, 'administrative_area_level_3') || getAddrComponent(comps, 'administrative_area_level_2');
    const pincode  = getAddrComponent(comps, 'postal_code');
    const state    = getAddrComponent(comps, 'administrative_area_level_1');
    const country  = getAddrComponent(comps, 'country');
    if (city)     set('City', city);
    if (district) set('District', district);
    if (pincode)  set('Pincode', pincode);
    if (state)    set('State', state);
    if (country)  set('Country', country);
  };

  return (
    <div style={{
      background: '#F8FAFC', border: '1.5px solid #A5F3FC',
      borderRadius: 10, padding: '14px 14px 4px', marginBottom: 14,
    }}>
      <p style={{
        fontSize: 12, fontWeight: 700, color: '#0E7490',
        textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 12px',
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        <span style={{ fontSize: 14 }}>📍</span>
        Address Details
        <span style={{
          fontSize: 9, fontWeight: 600, color: '#06B6D4',
          background: '#ECFEFF', borderRadius: 20, padding: '2px 8px',
          border: '1px solid #A5F3FC', textTransform: 'none', letterSpacing: 0,
        }}>
          powered by Google Maps
        </span>
      </p>

      <PlacesInput
        label="Street Address / House No."
        value={data.Address || ''}
        onChange={(val) => set('Address', val)}
        onPlaceSelected={handlePlaceSelected}
        placeholder="Start typing your address..."
        icon="🏠" types={['address']}
        componentRestrictions={{ country: 'in' }}
      />
      <div className="two-col">
        <PlacesInput label="City" value={data.City || ''} onChange={(val) => set('City', val)}
          placeholder="Search city..." icon="🏙️" types={['(cities)']} componentRestrictions={{ country: 'in' }} />
        <PlacesInput label="District" value={data.District || ''} onChange={(val) => set('District', val)}
          placeholder="District name..." icon="🗺️" componentRestrictions={{ country: 'in' }} />
      </div>
      <div className="two-col">
        <PlacesInput label="Pincode" value={data.Pincode || ''} onChange={(val) => set('Pincode', val)}
          placeholder="6-digit pincode..." icon="📮" types={['postal_code']} componentRestrictions={{ country: 'in' }} />
        <PlacesInput label="State" value={data.State || ''} onChange={(val) => set('State', val)}
          placeholder="Search state..." icon="🏛️" types={['administrative_area_level_1']} componentRestrictions={{ country: 'in' }} />
      </div>
      <PlacesInput label="Country" value={data.Country || 'India'} onChange={(val) => set('Country', val)}
        placeholder="Country..." icon="🌏" types={['country']} componentRestrictions={null} />
    </div>
  );
};

// ── Main Register Component ──────────────────────────────────────────────────
const Register = () => {
  const isMobile = useIsMobile();
  const [selectedRole, setSelectedRole] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState({});
  const [doctorStep, setDoctorStep] = useState(null);

  const [patientData, setPatientData] = useState({
    First: '', Middle: '', Last: '', Email: '', Mobile: '', Password: '', ConfirmPassword: '',
  });
  const [doctorData, setDoctorData] = useState({
    ClinicName: '', LicenseNumber: '', Education: '', Email: '',
    First: '', Last: '', Mobile: '', Speciality: [],
    Address: '', City: '', District: '', Pincode: '', State: '', Country: 'India',
    Password: '', ConfirmPassword: '',
  });
  const [hospitalData, setHospitalData] = useState({
    HospitalName: '', RegistrationNumber: '', Email: '', Mobile: '',
    Address: '', City: '', District: '', Pincode: '', State: '', Country: 'India',
    Password: '', ConfirmPassword: '',
  });

  const validateEmail    = (v) => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(v);
  const validatePassword = (v) => /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(v);
  const validateMobile   = (v) => /^[6-9]\d{9}$/.test(v);
  const validateName     = (v) => /^[a-zA-Z\s]*$/.test(v.trim());
  const validatePincode  = (v) => /^\d{6}$/.test(v);

  const handleChange = (setter) => (e) => setter(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const validateAddress = (d, label = '') => {
    if (!d.Address)  { toast.error(`${label}Street address is required`); return false; }
    if (!d.District) { toast.error(`${label}District is required`); return false; }
    if (!d.Pincode || !validatePincode(d.Pincode)) { toast.error(`${label}Valid 6-digit pincode is required`); return false; }
    if (!d.State)    { toast.error(`${label}State is required`); return false; }
    if (!d.Country)  { toast.error(`${label}Country is required`); return false; }
    return true;
  };

  const handleDoctorSubmit = async () => {
    const d = doctorData;
    if (!d.First || !d.Last)                              { toast.error('First and Last name are required'); return; }
    if (!validateName(d.First) || !validateName(d.Last))  { toast.error('Names must not contain numbers or special characters'); return; }
    if (!validateEmail(d.Email))                          { toast.error('Please enter a valid email address'); return; }
    if (!validateMobile(d.Mobile))                        { toast.error('Please enter a valid 10-digit mobile number starting with 6-9'); return; }
    if (!d.ClinicName)                                    { toast.error('Clinic name is required'); return; }
    if (!d.LicenseNumber)                                 { toast.error('License number is required'); return; }
    if (!Array.isArray(d.Speciality) || d.Speciality.length === 0) { toast.error('Please add at least one speciality'); return; }
    if (!validateAddress(d))                               return;
    if (!validatePassword(d.Password))                    { toast.error('Password must be at least 8 characters with a letter, number and special character'); return; }
    if (d.Password !== d.ConfirmPassword)                 { toast.error('Passwords do not match'); return; }

    setLoading(true);
    try {
      const { getAuth, createUserWithEmailAndPassword } = await import('firebase/auth');
      const { getDatabase, ref, set } = await import('firebase/database');
      const { app } = await import('../../../config/Firebase/firebase.config');

      const auth   = getAuth(app);
      const fireDB = getDatabase(app);
      const userCredential = await createUserWithEmailAndPassword(auth, d.Email, d.Password);
      const uid = userCredential.user.uid;

      await set(ref(fireDB, `doctor/${uid}`), {
        First: d.First, Last: d.Last, Email: d.Email, Mobile: d.Mobile,
        ClinicName: d.ClinicName, LicenseNumber: d.LicenseNumber,
        Education: d.Education, Speciality: d.Speciality,
        Address: d.Address, City: d.City, District: d.District,
        Pincode: d.Pincode, State: d.State, Country: d.Country,
        uid, role: 'doctor', subscriptionStatus: 'free',
        createdAt: new Date().toISOString(),
      });

      localStorage.setItem('userId', uid);
      localStorage.setItem('userRole', 'doctor');
      localStorage.setItem('userEmail', d.Email);
      localStorage.setItem('userName', `Dr. ${d.First} ${d.Last}`);
      localStorage.setItem('profileIncomplete', 'true');

      toast.success('🎉 Registration complete! Your account is created. Complete your profile for more appointments.');
      setTimeout(() => { window.location.href = `/profile/${uid}`; }, 2000);

      setLoading(false);
      setSelectedRole(null);
      setDoctorData({
        ClinicName: '', LicenseNumber: '', Education: '', Email: '',
        First: '', Last: '', Mobile: '', Speciality: [],
        Address: '', City: '', District: '', Pincode: '', State: '', Country: 'India',
        Password: '', ConfirmPassword: '',
      });
    } catch (error) {
      setLoading(false);
      if (error.code === 'auth/email-already-in-use')  toast.error('This email is already registered. Please use a different email or login.');
      else if (error.code === 'auth/invalid-email')    toast.error('Invalid email address. Please check and try again.');
      else if (error.code === 'auth/weak-password')    toast.error('Password is too weak. Use at least 8 characters with letters, numbers and symbols.');
      else toast.error(`Registration failed (${error.code || 'unknown'}): ${error.message}`);
    }
  };

  const handleSubmit = async (role) => {
    if (role === 'doctor') { handleDoctorSubmit(); return; }
    const data = role === 'patient' ? patientData : hospitalData;

    if (role === 'patient') {
      if (!data.First || !data.Middle || !data.Last) { toast.error('All name fields are required'); return; }
      if (!validateName(data.First) || !validateName(data.Middle) || !validateName(data.Last)) {
        toast.error('Names must not contain numbers or special characters'); return;
      }
    }
    if (role === 'hospital') {
      if (!data.HospitalName)       { toast.error('Hospital name is required'); return; }
      if (!data.RegistrationNumber) { toast.error('Registration number is required'); return; }
      if (!validateAddress(data))    return;
    }

    if (!validateEmail(data.Email))             { toast.error('Please enter a valid email address'); return; }
    if (!validateMobile(data.Mobile))           { toast.error('Please enter a valid 10-digit mobile number'); return; }
    if (!validatePassword(data.Password))       { toast.error('Password must be at least 8 characters with a letter, number and special character'); return; }
    if (data.Password !== data.ConfirmPassword) { toast.error('Passwords do not match'); return; }

    setLoading(true);
    try {
      const { getAuth, createUserWithEmailAndPassword } = await import('firebase/auth');
      const { getDatabase, ref, set } = await import('firebase/database');
      const { app } = await import('../../../config/Firebase/firebase.config');

      const auth   = getAuth(app);
      const fireDB = getDatabase(app);
      const cred   = await createUserWithEmailAndPassword(auth, data.Email, data.Password);
      const uid    = cred.user.uid;

      if (role === 'patient') {
        await set(ref(fireDB, `users/${uid}`), {
          First: data.First, Middle: data.Middle, Last: data.Last,
          Email: data.Email, Mobile: data.Mobile,
          uid, role: 'patient', createdAt: new Date().toISOString(),
        });
      } else {
        await set(ref(fireDB, `hospitals/${uid}`), {
          HospitalName: data.HospitalName, RegistrationNumber: data.RegistrationNumber,
          Email: data.Email, Mobile: data.Mobile,
          Address: data.Address, City: data.City, District: data.District,
          Pincode: data.Pincode, State: data.State, Country: data.Country,
          uid, role: 'hospital', createdAt: new Date().toISOString(),
        });
      }

      localStorage.setItem('userId', uid);
      localStorage.setItem('userRole', role);
      localStorage.setItem('userEmail', data.Email);
      localStorage.setItem('userName', role === 'patient' ? `${data.First} ${data.Last}` : data.HospitalName);

      toast.success(`${role.charAt(0).toUpperCase() + role.slice(1)} registration successful!`);
      setTimeout(() => { window.location.href = `/${role}-dashboard`; }, 2000);

      setLoading(false);
      setSelectedRole(null);
      if (role === 'patient')  setPatientData({ First: '', Middle: '', Last: '', Email: '', Mobile: '', Password: '', ConfirmPassword: '' });
      if (role === 'hospital') setHospitalData({ HospitalName: '', RegistrationNumber: '', Email: '', Mobile: '', Address: '', City: '', District: '', Pincode: '', State: '', Country: 'India', Password: '', ConfirmPassword: '' });
    } catch (error) {
      setLoading(false);
      if (error.code === 'auth/email-already-in-use') toast.error('Email already registered.');
      else toast.error('Registration failed: ' + error.message);
    }
  };

  const ip = (name, role, data, setter) => ({
    name, value: data[name],
    onChange: handleChange(setter),
    fieldKey: `${role}-${name}`,
    showPassword, setShowPassword,
  });

  // ── Role Selection Page ──────────────────────────────────────────────────────
  if (!selectedRole) {
    return (
      <div className="register-container">
        <GlobalStyle />
        <ToastContainer position={isMobile ? 'bottom-center' : 'top-right'} />

        <div className="register-content">
          {/* Header */}
          <div style={{ marginBottom: isMobile ? 28 : 36 }}>
            <div className="register-header-badge">
              🏥 Healthcare Platform
            </div>
            <h1 className="register-title">
              Welcome to TrustYou Doctor
            </h1>
            <p className="register-subtitle">
              Join our comprehensive healthcare ecosystem
            </p>
          </div>

          <h2 style={{
            fontSize: isMobile ? 15 : 17, fontWeight: 600,
            textAlign: 'center', color: 'rgba(255,255,255,0.85)',
            marginBottom: isMobile ? 20 : 28, letterSpacing: '0.04em',
            textTransform: 'uppercase',
          }}>
            Choose Your Role
          </h2>

          <div className="role-cards-wrapper">
            {[
              { role: 'patient',  icon: '👤',    desc: 'Access healthcare services',  badge: null },
              { role: 'doctor',   icon: '👨‍⚕️', desc: 'Manage your practice',        badge: '₹1,200/yr' },
              { role: 'hospital', icon: '🏥',    desc: 'Streamline operations',       badge: null },
            ].map(({ role, icon, desc, badge }) => (
              <div key={role} className="role-card" onClick={() => setSelectedRole(role)}>
                {badge && <div className="role-badge">{badge}</div>}
                <span className="role-icon">{icon}</span>
                <h3>{role}</h3>
                <p>{desc}</p>
                <button className="role-card-btn">
                  Register as {role.charAt(0).toUpperCase() + role.slice(1)}
                </button>
              </div>
            ))}
          </div>

          <div className="already-registered">
            <p>
              Already have an account?{' '}
              <a href="/login" className="login-link">Login here →</a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Registration Form Modal ───────────────────────────────────────────────
  const COLOR = selectedRole === 'patient' ? '#3B82F6' : selectedRole === 'doctor' ? '#0D5C63' : '#10B981';

  return (
    <>
      <GlobalStyle />
      <ToastContainer position={isMobile ? 'bottom-center' : 'top-right'} />
      <div className="overlay-bg">
        <div className="modal-box">
          <div className="modal-header" style={mkHeader(COLOR)}>
            <h2 style={{ ...headerTitle, textTransform: 'capitalize' }}>{selectedRole} Registration</h2>
            <button onClick={() => { setSelectedRole(null); setDoctorStep(null); }} style={closeBtn}>✕</button>
          </div>

          <div className="form-inner">

            {/* ── Patient form ── */}
            {selectedRole === 'patient' && (
              <>
                <div className="two-col">
                  <InputField label="First Name"  {...ip('First',  'patient', patientData, setPatientData)} />
                  <InputField label="Middle Name" {...ip('Middle', 'patient', patientData, setPatientData)} />
                </div>
                <InputField label="Last Name"     {...ip('Last',   'patient', patientData, setPatientData)} />
                <InputField label="Email Address" type="email" {...ip('Email',   'patient', patientData, setPatientData)} />
                <InputField label="Mobile Number" type="tel" maxLength="10" {...ip('Mobile', 'patient', patientData, setPatientData)} />
                <InputField label="Password"         type="password" {...ip('Password',        'patient', patientData, setPatientData)} />
                <InputField label="Confirm Password" type="password" {...ip('ConfirmPassword', 'patient', patientData, setPatientData)} />
              </>
            )}

            {/* ── Doctor form ── */}
            {selectedRole === 'doctor' && (
              <>
                {/* Premium Features Banner */}
                <FeaturesBanner />

                <div className="two-col">
                  <InputField label="First Name" {...ip('First', 'doctor', doctorData, setDoctorData)} />
                  <InputField label="Last Name"  {...ip('Last',  'doctor', doctorData, setDoctorData)} />
                </div>
                <InputField label="Email Address" type="email" {...ip('Email',  'doctor', doctorData, setDoctorData)} />
                <InputField label="Mobile Number" type="tel" maxLength="10" {...ip('Mobile', 'doctor', doctorData, setDoctorData)} />
                <InputField label="Clinic Name"    {...ip('ClinicName',    'doctor', doctorData, setDoctorData)} />
                <InputField label="License Number" {...ip('LicenseNumber', 'doctor', doctorData, setDoctorData)} />
                <InputField label="Education"      {...ip('Education',     'doctor', doctorData, setDoctorData)} required={false} />
                <SpecialityAutosuggest
                  multi
                  value={doctorData.Speciality}
                  onChange={(arr) => setDoctorData(prev => ({ ...prev, Speciality: arr }))}
                />
                <AddressSection
                  data={doctorData}
                  onChange={handleChange(setDoctorData)}
                />
                <InputField label="Password"         type="password" {...ip('Password',        'doctor', doctorData, setDoctorData)} />
                <InputField label="Confirm Password" type="password" {...ip('ConfirmPassword', 'doctor', doctorData, setDoctorData)} />
              </>
            )}

            {/* ── Hospital form ── */}
            {selectedRole === 'hospital' && (
              <>
                <InputField label="Hospital Name"       {...ip('HospitalName',       'hospital', hospitalData, setHospitalData)} />
                <InputField label="Registration Number" {...ip('RegistrationNumber', 'hospital', hospitalData, setHospitalData)} />
                <InputField label="Email Address" type="email" {...ip('Email',  'hospital', hospitalData, setHospitalData)} />
                <InputField label="Mobile Number" type="tel" maxLength="10" {...ip('Mobile', 'hospital', hospitalData, setHospitalData)} />
                <AddressSection
                  data={hospitalData}
                  onChange={handleChange(setHospitalData)}
                />
                <InputField label="Password"         type="password" {...ip('Password',        'hospital', hospitalData, setHospitalData)} />
                <InputField label="Confirm Password" type="password" {...ip('ConfirmPassword', 'hospital', hospitalData, setHospitalData)} />
              </>
            )}

            <button
              onClick={() => handleSubmit(selectedRole)}
              disabled={loading}
              style={{
                width: '100%', marginTop: 18, padding: '14px', borderRadius: 10,
                fontWeight: 700, color: 'white', fontSize: 15, border: 'none', minHeight: 48,
                cursor: loading ? 'not-allowed' : 'pointer',
                backgroundColor: loading ? '#9CA3AF' : COLOR,
                boxShadow: loading ? 'none' : `0 4px 14px ${COLOR}55`,
              }}
            >
              {loading ? 'Processing...' : selectedRole === 'doctor' ? '✅ Complete Registration' : 'Complete Registration'}
            </button>

            <p style={{ textAlign: 'center', marginTop: 14, fontSize: 13, color: '#6B7280' }}>
              Already have an account?{' '}
              <a href="/login" style={{ color: '#2563EB', fontWeight: 600, textDecoration: 'none' }}>
                Go to Login →
              </a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Register;